import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from 'src/app/shared/common.service';
import {
  IConditionFieldDescriptor,
  IEnumOption,
  IWorkflowAction,
  IWorkflowActionDescriptor,
  IWorkflowCondition,
  IWorkflowConditionGroup,
  IWorkflowExecution,
  IWorkflowRule,
  IWorkflowSchema,
  WorkflowActionType,
  WorkflowExecutionStatus,
  WorkflowRuleStatus,
  WorkflowTriggerType,
} from '../../models/workflows.models';
import { WorkflowsService } from '../../services/workflows.service';
import {
  executionTargetRef,
  executionTargetUrl,
} from '../../utils/workflow-execution-link.util';

let nextId = 1;
function uid(): string {
  return `tmp-${nextId++}-${Math.random().toString(36).slice(2, 8)}`;
}

/** "Last runs" preview window: how far back to count, how many to fetch, how many to show. */
const LAST_RUNS_WINDOW_DAYS = 7;
const LAST_RUNS_FETCH_LIMIT = 50;
const LAST_RUNS_DISPLAY_LIMIT = 6;

/** Fields whose selector label is intentionally bare (their group header disambiguates them),
 *  but which need a group-qualified label in the flat preview — otherwise `task.status` and
 *  `order.status` would both read "Status". Keep in sync with the backend preview (preview.py). */
const PREVIEW_FIELD_LABEL_OVERRIDES: Record<string, string> = {
  'task.status': 'workflows.fields.task_status_preview',
  'order.status': 'workflows.fields.order_status_preview',
};

@Component({
  selector: 'app-workflow-builder-step',
  templateUrl: './workflow-builder-step.component.html',
  styleUrls: ['./workflow-builder-step.component.scss'],
})
export class WorkflowBuilderStepComponent implements OnInit, OnDestroy {
  @Input() rule!: Partial<IWorkflowRule>;
  @Input() schema!: IWorkflowSchema;
  @Input() origin: 'manual' | 'template' | 'ai' | 'edit' | null = null;

  @Output() ruleChange = new EventEmitter<Partial<IWorkflowRule>>();
  @Output() save = new EventEmitter<{ targetStatus: WorkflowRuleStatus }>();

  nameControl = new FormControl<string>('', { nonNullable: true });
  descriptionControl = new FormControl<string>('', { nonNullable: true });
  stopProcessingControl = new FormControl<boolean>(false, { nonNullable: true });

  /** Cached enum values by field key. */
  enumCache: Record<string, IEnumOption[]> = {};

  /** Recent executions shown in the preview's "Last runs" list (edit mode only). */
  lastRuns: IWorkflowExecution[] = [];
  /** Number of executions within the trailing window (drives the "N in the last 7 days" label). */
  lastRunsCount = 0;
  lastRunsLoading = false;

  /** Redirect target + short ref for a run row (order or thread); used by the template. */
  readonly runTargetUrl = executionTargetUrl;
  readonly runTargetRef = executionTargetRef;

  /** Memoized per-trigger scoping results. These back template bindings, so they must keep a
   *  stable array reference across change-detection cycles — a fresh array every CD re-fires the
   *  child rows' ngOnChanges and loops. Recomputed only when the trigger or schema changes. */
  private conditionFieldsCache: {
    triggerType?: WorkflowTriggerType;
    schema?: IWorkflowSchema;
    fields: IConditionFieldDescriptor[];
  } | null = null;
  private actionDescriptorsCache: {
    triggerType?: WorkflowTriggerType;
    schema?: IWorkflowSchema;
    descriptors: IWorkflowActionDescriptor[];
  } | null = null;
  /** Same stable-reference contract, keyed on the locked fields too since those
   *  change as the rule is edited, not only with the trigger or schema. */
  private selectableConditionFieldsCache: {
    fields: IConditionFieldDescriptor[];
    lockedKey: string;
    selectable: IConditionFieldDescriptor[];
  } | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private commonService: CommonService,
    private workflowsService: WorkflowsService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.nameControl.setValue(this.rule.name ?? '', { emitEvent: false });
    this.descriptionControl.setValue(this.rule.description ?? '', { emitEvent: false });
    this.stopProcessingControl.setValue(this.rule.stopProcessing ?? false, { emitEvent: false });

    this.nameControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onNameChange());

    this.descriptionControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onDescriptionChange());

    // Pre-load enum values for select-backed condition fields.
    for (const field of this.schema?.conditionFields ?? []) {
      if ((field.dataType === 'enum' || field.dataType === 'multi_enum') && field.key) {
        this.loadEnumValues(field.key);
      }
    }

    // Pre-load enum values for select-backed action params. Some option fields
    // (e.g. order.owner for set_owner) are exposed only through an action's
    // optionsEndpoint and never appear as a condition field, so they would not be
    // loaded by the condition loop above and the select would render empty.
    for (const action of this.schema?.actions ?? []) {
      for (const param of action.params ?? []) {
        if (param.controlType === 'select' && param.optionsEndpoint) {
          const fieldKey = this.enumFieldKeyFromEndpoint(param.optionsEndpoint);
          if (fieldKey) {
            this.loadEnumValues(fieldKey);
          }
        }
        // The composite "Modify order details" control declares its own option
        // endpoints on nested orderFields (e.g. order.location for the location
        // picker); preload those too so the sub-selects aren't empty.
        if (param.controlType === 'order_field_modifications') {
          for (const orderField of param.orderFields ?? []) {
            const fieldKey = this.enumFieldKeyFromEndpoint(orderField.optionsEndpoint ?? '');
            if (fieldKey) {
              this.loadEnumValues(fieldKey);
            }
          }
        }
      }
    }

    if (this.isEditMode && this.rule.id) {
      this.loadLastRuns(this.rule.id);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Trigger ───────────────────────────────────────────────────────────

  onTriggerChanged(type: WorkflowTriggerType): void {
    // Conditions/actions that aren't valid for the new trigger are dropped
    // silently so the rule stays consistent (no warning toast).
    const filteredActions = this.withAllowedActionsForTrigger(type, this.actions);
    const filteredGroups = this.withAllowedConditionsForTrigger(type, this.conditionGroups);

    this.patchRule({
      trigger: { type, params: {} },
      actions: filteredActions,
      conditionGroups: filteredGroups,
    });
  }

  // ── Conditions ────────────────────────────────────────────────────────

  get conditionGroups(): IWorkflowConditionGroup[] {
    return this.rule.conditionGroups ?? [];
  }

  /** For V1 we use a single condition group (all conditions AND'd). */
  get conditions(): IWorkflowCondition[] {
    return this.conditionGroups[0]?.conditions ?? [];
  }

  /** Condition fields offered for the selected trigger (respects its conditionCategories).
   *  Memoized to a stable reference; see conditionFieldsCache. */
  get availableConditionFields(): IConditionFieldDescriptor[] {
    const triggerType = this.rule.trigger?.type;
    if (
      !this.conditionFieldsCache ||
      this.conditionFieldsCache.triggerType !== triggerType ||
      this.conditionFieldsCache.schema !== this.schema
    ) {
      this.conditionFieldsCache = {
        triggerType,
        schema: this.schema,
        fields: this.conditionFieldsForTrigger(triggerType),
      };
    }
    return this.conditionFieldsCache.fields;
  }

  addCondition(): void {
    // Seeded from the selectable list, never the full one: defaulting to a field a
    // locked condition already pins would create the duplicate this guards against.
    const defaultField = this.selectableConditionFields[0];
    if (!defaultField) {
      return;
    }
    const defaultOp = defaultField.operators?.[0] ?? 'equals';
    const newCondition: IWorkflowCondition = {
      id: uid(),
      field: defaultField.key,
      operator: defaultOp as IWorkflowCondition['operator'],
      value: '',
    };
    const groups = this.conditionGroups.length
      ? this.conditionGroups.map((g, i) =>
          i === 0
            ? { ...g, conditions: [...g.conditions, newCondition] }
            : g
        )
      : [{ id: uid(), match: 'all' as const, conditions: [newCondition] }];
    this.patchRule({ conditionGroups: groups });
  }

  onConditionChanged(updated: IWorkflowCondition): void {
    const groups = this.conditionGroups.map((g) => ({
      ...g,
      conditions: g.conditions.map((c) => (c.id === updated.id ? updated : c)),
    }));
    this.patchRule({ conditionGroups: groups });
  }

  onConditionRemoved(id: string): void {
    const groups = this.conditionGroups
      .map((g) => ({
        ...g,
        conditions: g.conditions.filter((c) => c.id !== id),
      }))
      .filter((g) => g.conditions.length > 0);
    this.patchRule({ conditionGroups: groups });
  }

  // ── Actions ───────────────────────────────────────────────────────────

  get actions(): IWorkflowAction[] {
    return this.rule.actions ?? [];
  }

  /** Action descriptors offered in the row's type dropdown.
   *
   *  Excludes `templateOnly` descriptors, so a template-only action can never be
   *  picked when building a rule by hand. Note this is NOT the same set as
   *  `actionDescriptorsForTrigger`, which stays the allowed-ness predicate used
   *  for pruning on a trigger change — a locked action must survive that.
   *  Rows resolve their own descriptor from `schema.actions` via
   *  `[descriptorCatalog]` instead, so a locked row still renders.
   *
   *  Memoized to a stable reference; see actionDescriptorsCache. */
  get allowedActionDescriptors(): IWorkflowActionDescriptor[] {
    const triggerType = this.rule.trigger?.type;
    if (
      !this.actionDescriptorsCache ||
      this.actionDescriptorsCache.triggerType !== triggerType ||
      this.actionDescriptorsCache.schema !== this.schema
    ) {
      this.actionDescriptorsCache = {
        triggerType,
        schema: this.schema,
        descriptors: this.actionDescriptorsForTrigger(triggerType).filter(
          (descriptor) => !descriptor.templateOnly
        ),
      };
    }
    return this.actionDescriptorsCache.descriptors;
  }

  /** Every action descriptor, for row-level descriptor *lookup* (not the picker). */
  get actionDescriptorCatalog(): IWorkflowActionDescriptor[] {
    return this.schema?.actions ?? [];
  }

  // ── Locked (template-derived) elements ────────────────────────────────
  //
  // Lock state is DERIVED from the rule + schema, never persisted and never
  // carried in the template payload. One code path therefore serves both a
  // freshly materialised template and a reopened saved rule, with no marker to
  // keep in sync and no migration.

  /** Descriptors of the templateOnly actions currently on the rule. */
  private get templateOnlyDescriptors(): IWorkflowActionDescriptor[] {
    const present = new Set(this.actions.map((action) => action.type));
    return this.actionDescriptorCatalog.filter(
      (descriptor) => descriptor.templateOnly && present.has(descriptor.type)
    );
  }

  /** An action is locked when its own descriptor is templateOnly. */
  isActionLocked(action: IWorkflowAction): boolean {
    return !!this.actionDescriptorCatalog.find((d) => d.type === action.type)?.templateOnly;
  }

  /** The id of the ONE condition locked per requiredConditions entry of a
   *  templateOnly action on the rule — removing or editing it would make that
   *  action unsavable.
   *
   *  Only the first match is claimed. Matching on field/operator/value alone
   *  locked every copy, so a second, user-added condition with the same triple
   *  came out locked as well: undeletable, and leaving the rule carrying the same
   *  condition twice. `selectableConditionFields` keeps that duplicate from being
   *  created in the first place; this is what makes one recoverable if it exists
   *  already (an AI draft or an API write can still produce it). */
  private get lockedConditionIds(): Set<string> {
    const locked = new Set<string>();
    for (const descriptor of this.templateOnlyDescriptors) {
      for (const required of descriptor.requiredConditions ?? []) {
        const match = this.conditions.find(
          (condition) =>
            !locked.has(condition.id) &&
            condition.field === required.field &&
            condition.operator === required.operator &&
            condition.value === required.value
        );
        if (match) {
          locked.add(match.id);
        }
      }
    }
    return locked;
  }

  isConditionLocked(condition: IWorkflowCondition): boolean {
    return this.lockedConditionIds.has(condition.id);
  }

  /** Fields already pinned by a locked condition. */
  private get lockedConditionFields(): Set<string> {
    const lockedIds = this.lockedConditionIds;
    return new Set(
      this.conditions.filter((condition) => lockedIds.has(condition.id)).map((condition) => condition.field)
    );
  }

  /** Fields an editable condition may pick. A field a locked condition already
   *  pins is excluded: on an AND-only group a second condition on it is redundant
   *  or contradictory, and it used to come out locked and unremovable. */
  get selectableConditionFields(): IConditionFieldDescriptor[] {
    const fields = this.availableConditionFields;
    const lockedFields = this.lockedConditionFields;
    const lockedKey = [...lockedFields].sort().join('|');
    if (
      !this.selectableConditionFieldsCache ||
      this.selectableConditionFieldsCache.fields !== fields ||
      this.selectableConditionFieldsCache.lockedKey !== lockedKey
    ) {
      this.selectableConditionFieldsCache = {
        fields,
        lockedKey,
        // No locked fields: hand back the very same array, so the rows keep the
        // identity they already hold.
        selectable: lockedFields.size
          ? fields.filter((field) => !lockedFields.has(field.key))
          : fields,
      };
    }
    return this.selectableConditionFieldsCache.selectable;
  }

  /** A locked row keeps the unfiltered list so its own field still resolves a
   *  label and operators; editable rows get the filtered one. Both sides are
   *  memoised, so a row's options reference stays stable. */
  conditionFieldsFor(condition: IWorkflowCondition): IConditionFieldDescriptor[] {
    return this.isConditionLocked(condition)
      ? this.availableConditionFields
      : this.selectableConditionFields;
  }

  /** False once every field the trigger allows is pinned by a locked condition. */
  get canAddCondition(): boolean {
    return this.selectableConditionFields.length > 0;
  }

  /** The trigger is locked while any templateOnly action is present: its
   *  `allowedTriggers` pins it anyway, so letting it change could only produce an
   *  invalid rule. A template rule the user no longer wants is paused or
   *  archived, not rewired. */
  get isTriggerLocked(): boolean {
    return this.templateOnlyDescriptors.length > 0;
  }

  /** Whether every templateOnly action's required conditions are all present.
   *  Belt-and-braces for `canSave`: the UI cannot remove a locked row, so this
   *  should always hold — it stops the save button ever producing a payload the
   *  server would reject. */
  private get requiredConditionsSatisfied(): boolean {
    const groups = this.conditionGroups;
    return this.templateOnlyDescriptors.every((descriptor) =>
      (descriptor.requiredConditions ?? []).every(
        (required) =>
          groups.length > 0 &&
          groups.every((group) =>
            group.conditions.some(
              (condition) =>
                condition.field === required.field &&
                condition.operator === required.operator &&
                condition.value === required.value
            )
          )
      )
    );
  }

  addAction(): void {
    const defaultActionDescriptor = this.allowedActionDescriptors[0];
    if (!defaultActionDescriptor) {
      return;
    }

    const newAction: IWorkflowAction = {
      id: uid(),
      type: defaultActionDescriptor.type as WorkflowActionType,
      params: {},
      order: this.actions.length,
    };
    this.patchRule({ actions: this.withNormalizedActionOrder([...this.actions, newAction]) });
  }

  onActionChanged(updated: IWorkflowAction): void {
    const actions = this.actions.map((a) => (a.id === updated.id ? updated : a));
    this.patchRule({ actions });
  }

  onActionRemoved(id: string): void {
    const actions = this.withNormalizedActionOrder(this.actions.filter((a) => a.id !== id));
    this.patchRule({ actions });
  }

  onActionDrop(event: CdkDragDrop<IWorkflowAction[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const reorderedActions = [...this.actions];
    moveItemInArray(reorderedActions, event.previousIndex, event.currentIndex);
    this.patchRule({ actions: this.withNormalizedActionOrder(reorderedActions) });
  }

  // ── Meta fields ───────────────────────────────────────────────────────

  onNameChange(): void {
    this.patchRule({ name: this.nameControl.value });
  }

  onDescriptionChange(): void {
    this.patchRule({ description: this.descriptionControl.value || null });
  }

  onStopProcessingChange(): void {
    this.patchRule({ stopProcessing: this.stopProcessingControl.value });
  }

  // ── Preview ───────────────────────────────────────────────────────────

  /** The selected trigger, resolved to a display label for the preview panel. */
  get previewTrigger(): { label: string } | null {
    const trigger = this.schema?.triggers?.find((tr) => tr.type === this.rule.trigger?.type);
    if (!trigger) {
      return null;
    }
    return { label: this.translate.instant(trigger.i18nLabel) };
  }

  /** One structured line per condition (field, operator, value) for the preview.
   *  Rendered with text interpolation (auto-escaped), so user-entered values and
   *  resolved enum labels can never inject markup. */
  get previewConditions(): Array<{ field: string; operator: string; value: string }> {
    return this.conditions
      .filter((c) => c.field)
      .map((c) => {
        const fieldDesc = this.schema?.conditionFields?.find((f) => f.key === c.field);
        const labelKey = PREVIEW_FIELD_LABEL_OVERRIDES[c.field] ?? fieldDesc?.i18nLabel;
        const field = labelKey ? this.translate.instant(labelKey) : c.field;
        const operator = this.translate.instant(`workflows.operators.${c.operator}`);
        const value = this.formatConditionValue(fieldDesc, c.value);
        return { field, operator, value };
      });
  }

  /** One display label per action, in execution order. */
  get previewActions(): string[] {
    return this.actions
      .filter((a) => a.type)
      .map((a) => {
        const desc = this.schema?.actions?.find((d) => d.type === a.type);
        return desc ? this.translate.instant(desc.i18nLabel) : a.type;
      });
  }

  get hasPreviewContent(): boolean {
    return !!(this.previewTrigger || this.previewConditions.length || this.previewActions.length);
  }

  /** True when editing a rule that is not currently active (drives the preview status note). */
  get isInactive(): boolean {
    return this.isEditMode && this.currentStatus !== 'active';
  }

  // ── Last runs ───────────────────────────────────────────────────────────

  /** Human-readable label for an execution status (reuses the history namespace). */
  runStatusLabel(status: WorkflowExecutionStatus): string {
    return this.translate.instant(`workflows.history.status.${status}`);
  }

  /** Fetch this rule's recent executions for the preview's "Last runs" list. */
  private loadLastRuns(ruleId: string): void {
    this.lastRunsLoading = true;
    this.workflowsService
      .getRuleHistory(ruleId, 1, LAST_RUNS_FETCH_LIMIT)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const recent = this.withinLastRunsWindow(response.data ?? []);
          this.lastRunsCount = recent.length;
          this.lastRuns = recent.slice(0, LAST_RUNS_DISPLAY_LIMIT);
          this.lastRunsLoading = false;
        },
        error: () => {
          this.lastRunsLoading = false;
        },
      });
  }

  /** Executions within the trailing "last runs" window, newest-first order preserved. */
  private withinLastRunsWindow(executions: IWorkflowExecution[]): IWorkflowExecution[] {
    const cutoff = Date.now() - LAST_RUNS_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return executions.filter((execution) => {
      const executedAt = new Date(execution.executedAt).getTime();
      return !Number.isNaN(executedAt) && executedAt >= cutoff;
    });
  }

  // ── Save ──────────────────────────────────────────────────────────────

  get canSave(): boolean {
    return !!(
      this.rule.name?.trim() &&
      this.rule.trigger?.type &&
      this.actions.length > 0 &&
      this.actions.every((a) => !!a.type && this.actionParamsComplete(a)) &&
      this.requiredConditionsSatisfied
    );
  }

  /** Whether every `required` param of an action carries a non-empty value.
   *  Gates save so a rule can't be persisted/activated with an incomplete
   *  action that would then silently no-op on every run — e.g. a "Forward to
   *  fixed recipients" with no recipients, or a "Modify order details" picker
   *  left untouched (which emits an empty modifications object / no value). */
  private actionParamsComplete(action: IWorkflowAction): boolean {
    const descriptor = (this.schema?.actions ?? []).find((d) => d.type === action.type);
    if (!descriptor) return true;
    return descriptor.params
      .filter((param) => param.required)
      .every((param) => this.isParamValuePresent(action.params?.[param.key]));
  }

  private isParamValuePresent(value: unknown): boolean {
    if (value == null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  }

  get isEditMode(): boolean {
    return this.origin === 'edit';
  }

  get currentStatus(): WorkflowRuleStatus {
    return this.rule.status ?? 'draft';
  }

  get secondaryEditTargetStatus(): WorkflowRuleStatus {
    return this.currentStatus === 'active' ? 'paused' : 'active';
  }

  get secondaryEditLabelKey(): string {
    return this.currentStatus === 'active' ? 'common.deactivate' : 'common.activate';
  }

  onSave(targetStatus: WorkflowRuleStatus): void {
    if (this.hasDuplicateWorkflowEntries()) {
      this.showDuplicateEntriesToast();
      return;
    }

    this.save.emit({ targetStatus });
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  trackByConditionId(_i: number, c: IWorkflowCondition): string {
    return c.id;
  }

  trackByActionId(_i: number, a: IWorkflowAction): string {
    return a.id;
  }

  trackByRunId(_i: number, run: IWorkflowExecution): string {
    return run.id;
  }

  private patchRule(patch: Partial<IWorkflowRule>): void {
    this.rule = { ...this.rule, ...patch };
    this.ruleChange.emit(this.rule);
  }

  private withNormalizedActionOrder(actions: IWorkflowAction[]): IWorkflowAction[] {
    return actions.map((action, index) => ({
      ...action,
      order: index,
    }));
  }

  private withAllowedActionsForTrigger(
    triggerType: WorkflowTriggerType,
    actions: IWorkflowAction[]
  ): IWorkflowAction[] {
    const allowedTypes = new Set(
      this.actionDescriptorsForTrigger(triggerType).map((descriptor) => descriptor.type)
    );
    const allowedActions = actions.filter((action) => allowedTypes.has(action.type));

    return this.withNormalizedActionOrder(allowedActions);
  }

  private withAllowedConditionsForTrigger(
    triggerType: WorkflowTriggerType,
    groups: IWorkflowConditionGroup[]
  ): IWorkflowConditionGroup[] {
    const allowedKeys = new Set(
      this.conditionFieldsForTrigger(triggerType).map((field) => field.key)
    );

    return groups
      .map((group) => ({
        ...group,
        conditions: group.conditions.filter(
          (condition) => !condition.field || allowedKeys.has(condition.field)
        ),
      }))
      .filter((group) => group.conditions.length > 0);
  }

  /** Action descriptors offered for a trigger: not excluded, within the trigger's
   *  actionCategories (if declared), and — if the action declares a positive
   *  allowedTriggers whitelist — only for those triggers (e.g. forward_email is
   *  pinned to new_email_received). */
  private actionDescriptorsForTrigger(
    triggerType?: WorkflowTriggerType
  ): IWorkflowActionDescriptor[] {
    const actionCategories = this.triggerCategories(triggerType, 'actionCategories');

    return (this.schema?.actions ?? []).filter(
      (descriptor) =>
        (!triggerType || !descriptor.excludedTriggers?.includes(triggerType)) &&
        (!actionCategories ||
          (descriptor.category != null && actionCategories.includes(descriptor.category))) &&
        (!descriptor.allowedTriggers ||
          (!!triggerType && descriptor.allowedTriggers.includes(triggerType)))
    );
  }

  /** Condition fields offered for a trigger.
   *
   *  First the category whitelist applies (if declared). Then an optional per-field
   *  whitelist narrows *within* categories: a category that has any of its fields listed
   *  in `conditionFields` is reduced to only those fields, while categories with no entry
   *  keep all of theirs. So MTO can expose all customer/order fields but only the two
   *  email sender fields. */
  private conditionFieldsForTrigger(
    triggerType?: WorkflowTriggerType
  ): IConditionFieldDescriptor[] {
    const conditionCategories = this.triggerCategories(triggerType, 'conditionCategories');
    let fields = this.schema?.conditionFields ?? [];

    if (conditionCategories) {
      fields = fields.filter((field) => conditionCategories.includes(field.category));
    }

    const conditionFields = this.triggerConditionFields(triggerType);
    if (conditionFields.length) {
      const narrowedCategories = new Set(
        fields
          .filter((field) => conditionFields.includes(field.key))
          .map((field) => field.category)
      );
      fields = fields.filter(
        (field) =>
          !narrowedCategories.has(field.category) || conditionFields.includes(field.key)
      );
    }

    return fields;
  }

  /** The trigger's per-field whitelist (keys), or [] when it declares none. */
  private triggerConditionFields(triggerType?: WorkflowTriggerType): string[] {
    if (!triggerType) {
      return [];
    }
    const trigger = this.schema?.triggers?.find((t) => t.type === triggerType);
    return trigger?.conditionFields ?? [];
  }

  private triggerCategories(
    triggerType: WorkflowTriggerType | undefined,
    key: 'conditionCategories' | 'actionCategories'
  ): string[] | null {
    if (!triggerType) {
      return null;
    }
    const trigger = this.schema?.triggers?.find((t) => t.type === triggerType);
    return trigger?.[key] ?? null;
  }

  private hasDuplicateWorkflowEntries(): boolean {
    return this.hasDuplicateConditions() || this.hasDuplicateActions();
  }

  private hasDuplicateConditions(): boolean {
    const conditionKeys = this.conditions
      .filter((condition) => !!condition.field)
      .map((condition) => condition.field);

    return new Set(conditionKeys).size !== conditionKeys.length;
  }

  private hasDuplicateActions(): boolean {
    const actionKeys = this.actions
      .filter((action) => !!action.type)
      .map((action) => action.type);

    return new Set(actionKeys).size !== actionKeys.length;
  }

  /** Extract the `field=` key from an enum-values optionsEndpoint, e.g.
   *  `/v1/workflow-rules/enum-values?field=order.owner` → `order.owner`. */
  private enumFieldKeyFromEndpoint(optionsEndpoint: string): string {
    return optionsEndpoint.match(/field=([^&]+)/)?.[1] ?? '';
  }

  private loadEnumValues(fieldKey: string): void {
    if (this.enumCache[fieldKey]) return;
    this.workflowsService
      .getEnumValues(fieldKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((values) => {
        this.enumCache = { ...this.enumCache, [fieldKey]: values };
      });
  }

  private formatConditionValue(
    fieldDescriptor: IConditionFieldDescriptor | undefined,
    value: unknown
  ): string {
    if (value == null || value === '') {
      return '';
    }

    if (!fieldDescriptor) {
      return String(value);
    }

    if (fieldDescriptor.dataType === 'boolean') {
      return value
        ? this.translate.instant('common.yes')
        : this.translate.instant('common.no');
    }

    if (fieldDescriptor.dataType === 'enum') {
      if (Array.isArray(value)) {
        return value
          .map((entry) => this.resolveEnumLabel(fieldDescriptor.key, entry))
          .join(', ');
      }

      return this.resolveEnumLabel(fieldDescriptor.key, value);
    }

    if (fieldDescriptor.dataType === 'multi_enum') {
      if (!Array.isArray(value)) {
        return this.resolveEnumLabel(fieldDescriptor.key, value);
      }

      return value
        .map((entry) => this.resolveEnumLabel(fieldDescriptor.key, entry))
        .join(', ');
    }

    return String(value);
  }

  private resolveEnumLabel(fieldKey: string, value: unknown): string {
    const options = this.enumCache[fieldKey] ?? [];
    const matchedOption = options.find((option) => this.isSameEnumValue(option.value, value));

    return matchedOption?.label ?? String(value);
  }

  private isSameEnumValue(optionValue: IEnumOption['value'], value: unknown): boolean {
    return optionValue === value || String(optionValue) === String(value);
  }

  private showDuplicateEntriesToast(): void {
    const hasDuplicateConditions = this.hasDuplicateConditions();
    const hasDuplicateActions = this.hasDuplicateActions();
    let messageKey = 'workflows.builder.validation.duplicate_conditions_and_actions';

    if (hasDuplicateConditions && !hasDuplicateActions) {
      messageKey = 'workflows.builder.validation.duplicate_conditions';
    }

    if (hasDuplicateActions && !hasDuplicateConditions) {
      messageKey = 'workflows.builder.validation.duplicate_actions';
    }

    this.commonService.showToast(this.translate.instant(messageKey), 'WARNING');
  }
}
